import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { APP_BUILD } from "../config/version";

// A single-page app never refetches index.html on its own, so a tab left open
// keeps running whatever build it loaded — Tom's "I don't see the new version".
// This hook compares the running build with the one on the server
// (build/build.json, written by every build, served no-cache) and reloads at a
// safe moment: immediately when the page has been idle, on the next route
// change, or when the user clicks the banner. Inactive in local dev (no SHA).

const CHECK_EVERY_MS = 5 * 60 * 1000; // a 100-byte fetch
const IDLE_RELOAD_MS = 2 * 60 * 1000; // no pointer/keyboard activity for this long → safe to reload
const RELOADED_KEY = "telcofy:reloaded-for"; // guards against a reload loop if index.html and build.json disagree briefly
const SCROLL_KEY = "telcofy:reload-scroll"; // put the page back where it was after an automatic reload
const SNOOZE_KEY = "telcofy:update-snooze"; // { build, until } — "Later" really does mean later
const SNOOZE_MS = 24 * 60 * 60 * 1000;

/** A "Later" that's still in force for this build, if any. */
function snoozedUntil(build: string): number | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const { build: b, until } = JSON.parse(raw) as { build?: string; until?: number };
    // A newer build supersedes the snooze — it may be the fix someone is waiting for.
    if (b !== build || typeof until !== "number" || until <= Date.now()) return null;
    return until;
  } catch {
    return null;
  }
}

// A reload exits the browser's fullscreen and can't re-enter it without a user
// gesture, so a fullscreen map would silently drop back into the page. The
// banner is outside the map container, so it isn't visible in fullscreen
// either. While fullscreen, we wait — and re-check the moment it's exited.
const isFullscreen = () => !!document.fullscreenElement;

export function useAppUpdate() {
  const [newBuild, setNewBuild] = useState<string | null>(null);
  const [snoozeEnds, setSnoozeEnds] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(isFullscreen);
  const lastActivity = useRef(Date.now());
  const location = useLocation();
  const enabled = !!APP_BUILD;

  const reload = useCallback(() => {
    try {
      if (newBuild) sessionStorage.setItem(RELOADED_KEY, newBuild);
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    } catch {
      /* ignore */
    }
    window.location.reload();
  }, [newBuild]);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/build.json?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const { build } = (await res.json()) as { build?: string };
      if (build && build !== APP_BUILD) {
        setNewBuild(build);
        setSnoozeEnds(snoozedUntil(build)); // survives reloads and other tabs
      }
    } catch {
      /* offline or transient — try again next time */
    }
  }, []);

  // Poll, and re-check whenever the tab comes back into view or focus.
  useEffect(() => {
    if (!enabled) return;
    const first = setTimeout(check, 10 * 1000); // a tab restored by the browser may be on an old build already
    const id = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(first);
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [enabled, check]);

  // Re-render when fullscreen changes so the banner appears on exit.
  useEffect(() => {
    const onChange = () => setFullscreen(isFullscreen());
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Track activity so an automatic reload never interrupts someone mid-action.
  useEffect(() => {
    if (!enabled) return;
    const mark = () => (lastActivity.current = Date.now());
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "wheel", "touchstart"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, [enabled]);

  // Automatic reload once idle — once per detected build, so a brief mismatch
  // between index.html and build.json on the CDN can't loop. Never while a map
  // is fullscreen: the reload would yank the user out of it.
  useEffect(() => {
    if (!newBuild) return;
    let already = false;
    try {
      already = sessionStorage.getItem(RELOADED_KEY) === newBuild;
    } catch {
      /* ignore */
    }
    if (already) return;
    const id = setInterval(() => {
      if (isFullscreen()) return;
      if (snoozeEnds && Date.now() < snoozeEnds) return;
      if (snoozeEnds) setSnoozeEnds(null); // expired — the banner comes back too
      if (Date.now() - lastActivity.current >= IDLE_RELOAD_MS) reload();
    }, 15 * 1000);
    return () => clearInterval(id);
  }, [newBuild, reload, snoozeEnds]);

  // Put the page back where it was after an automatic reload.
  useEffect(() => {
    let y: string | null = null;
    try {
      y = sessionStorage.getItem(SCROLL_KEY);
      if (y) sessionStorage.removeItem(SCROLL_KEY);
    } catch {
      /* ignore */
    }
    if (y) requestAnimationFrame(() => window.scrollTo(0, Number(y)));
  }, []);

  // A route change is a natural moment to pick up the new build — unless a map
  // is fullscreen, in which case leaving it alone matters more.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    if (newBuild && !isFullscreen() && !(snoozeEnds && Date.now() < snoozeEnds)) reload();
  }, [location.pathname, newBuild, reload, snoozeEnds]);

  const snoozed = !!snoozeEnds && Date.now() < snoozeEnds;
  const dismiss = useCallback(() => {
    if (!newBuild) return;
    const until = Date.now() + SNOOZE_MS;
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify({ build: newBuild, until }));
    } catch {
      /* not remembered across reloads, but still quiet for this session */
    }
    setSnoozeEnds(until);
  }, [newBuild]);

  return { updateAvailable: !!newBuild && !snoozed && !fullscreen, newBuild, reload, dismiss };
}
